import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  Filter,
  ZoomIn,
  ZoomOut,
  RefreshCw,
  Download,
  User,
  Users,
  ChevronDown,
  ChevronRight,
  Crown,
  TrendingUp,
  DollarSign,
  Mail,
  Phone,
  MapPin,
  Menu,
  X,
  CheckCircle,
  XCircle,
  Layers,
  ChevronUp,
  ChevronLeft,
  ChevronsRight,
  Eye,
  EyeOff
} from 'lucide-react';
import { database, ref, get } from '../../firebase';

const GenealogyTree = () => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [selectedMember, setSelectedMember] = useState(null);
  const [viewMode, setViewMode] = useState('direct');
  const [searchTerm, setSearchTerm] = useState('');
  const [treeData, setTreeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [levels, setLevels] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [levelDisplayLimit, setLevelDisplayLimit] = useState(5);
  const [fullDataDepth, setFullDataDepth] = useState(11);
  const [showLevelInfo, setShowLevelInfo] = useState(false);
  const [showAllLevels, setShowAllLevels] = useState(false);
  const [allUsers, setAllUsers] = useState({});
  const [currentUserData, setCurrentUserData] = useState(null);
  const treeContainerRef = useRef(null);
  
  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
   
    checkMobile();
    window.addEventListener('resize', checkMobile);
   
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch all data
  useEffect(() => {
    fetchAllData();
  }, []);

  // Function to flatten users from date structure
  const flattenUsersFromDateStructure = (data) => {
    const flatUsers = {};
    
    if (!data) return flatUsers;
    
    Object.keys(data).forEach(dateKey => {
      const dateData = data[dateKey];
      if (dateData && typeof dateData === 'object') {
        Object.keys(dateData).forEach(userKey => {
          const user = dateData[userKey];
          if (user) {
            flatUsers[userKey] = {
              ...user,
              registrationDate: dateKey,
              dbKey: userKey
            };
          }
        });
      }
    });
    
    return flatUsers;
  };

  // Function to find user by sponsorId
  const findUserBySponsorId = (sponsorId, allUsersFlat) => {
    for (const [key, user] of Object.entries(allUsersFlat)) {
      if (user.sponsorId === sponsorId) {
        return { key, ...user };
      }
    }
    return null;
  };

  // Function to find user by dbKey
  const findUserByDbKey = (dbKey, allUsersFlat) => {
    return allUsersFlat[dbKey] ? { key: dbKey, ...allUsersFlat[dbKey] } : null;
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      
      console.log('Starting to fetch all data...');
      
      // Use Firebase instance instead of direct fetch
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      if (!snapshot.exists()) {
        setErrorMessage('No users found in database.');
        setLoading(false);
        return;
      }

      const rawData = snapshot.val();
      
      console.log('Raw data structure:', Object.keys(rawData));
      
      // Flatten users from date structure
      const flatUsers = flattenUsersFromDateStructure(rawData);
      
      console.log('Flattened users:', Object.keys(flatUsers).length, 'users');
      setAllUsers(flatUsers);
      
      // Get current user info from localStorage
      const currentUserId = localStorage.getItem('userSponsorId') || localStorage.getItem('userId');
      const currentUserDbKey = localStorage.getItem('dbKey');
      const currentUserRegDate = localStorage.getItem('registrationDate');
      
      console.log('Looking for user:', { currentUserId, currentUserDbKey, currentUserRegDate });
      
      let foundUserData = null;
      let foundUserKey = null;
      
      // Method 1: Find by dbKey (most reliable)
      if (currentUserDbKey && flatUsers[currentUserDbKey]) {
        foundUserKey = currentUserDbKey;
        foundUserData = flatUsers[currentUserDbKey];
        console.log('Found user by dbKey:', foundUserKey, foundUserData.name);
      }
      // Method 2: Find by sponsorId
      else if (currentUserId) {
        const found = findUserBySponsorId(currentUserId, flatUsers);
        if (found) {
          foundUserKey = found.key;
          foundUserData = found;
          console.log('Found user by sponsorId:', foundUserKey, foundUserData.name);
        }
      }
      
      if (!foundUserData) {
        console.log('User not found, showing first user as fallback');
        // Show first user as fallback
        const firstUserKey = Object.keys(flatUsers)[0];
        if (firstUserKey) {
          foundUserData = flatUsers[firstUserKey];
          foundUserKey = firstUserKey;
        }
      }
      
      if (!foundUserData) {
        setErrorMessage('No users found in database. Please add users first.');
        setLoading(false);
        return;
      }
      
      setCurrentUserData(foundUserData);
      
      // Build tree with current user as root (Level 0)
      const tree = buildTreeFromReferrals(foundUserKey, foundUserData, flatUsers);
      
      if (tree) {
        console.log('Tree built successfully:', {
          rootName: tree.name,
          rootId: tree.sponsorId,
          rootLevel: tree.level,
          directChildren: tree.children?.length || 0,
          rootStatus: tree.status
        });
        setTreeData(tree);
        setSelectedMember(tree);
        
        // Calculate level statistics
        const levelStats = calculateLevelStats(tree);
        setLevels(levelStats);
      } else {
        console.log('Building simple tree with root only');
        // Build a simple tree with just the root
        const simpleTree = {
          userId: foundUserKey,
          id: foundUserKey,
          name: foundUserData.name || 'Unknown',
          position: 'head',
          status: foundUserData.status === 'active' ? 'Active' : 'Inactive',
          package: getPackageName(foundUserData),
          level: 0,
          firebaseKey: foundUserKey,
          sponsorId: foundUserData.sponsorId || 'Unknown',
          referrals: foundUserData.referrals || [],
          children: [],
          hasActivePackage: foundUserData.status === 'active',
          userData: foundUserData,
          email: foundUserData.email || 'N/A',
          phone: foundUserData.mobile || 'N/A',
          location: foundUserData.country || 'N/A',
          registrationDate: foundUserData.registrationDate || 'N/A',
          showAllChildren: false
        };
        
        setTreeData(simpleTree);
        setSelectedMember(simpleTree);
        
        const levelStats = calculateLevelStats(simpleTree);
        setLevels(levelStats);
      }
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setErrorMessage(`Failed to load network tree: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getPackageName = (userData) => {
    if (!userData) return 'No Package';
    
    // Check if user has packages array
    if (userData.packages && Array.isArray(userData.packages)) {
      const activePackages = userData.packages.filter(pkg => pkg && pkg.status === 'active');
      if (activePackages.length > 0) {
        const latestPackage = activePackages[activePackages.length - 1];
        return latestPackage.packagePlan || latestPackage.packageName || 'Basic Package';
      }
    }
    
    // Check if user has status active but no packages
    if (userData.status === 'active') {
      return 'Active User';
    }
    
    return 'No Package';
  };

  // Build tree using referrals array (same as MyDirects.js)
  const buildTreeFromReferrals = (userKey, userData, allUsersData, depth = 0, maxDepth = 11, visited = new Set()) => {
    if (depth >= maxDepth || visited.has(userKey)) {
      return null;
    }
    
    visited.add(userKey);
    
    // Check if user is active
    const isActive = userData.status === 'active';
    
    console.log(`Processing user ${userData.name || userKey}:`, {
      status: userData.status,
      isActive: isActive,
      depth: depth,
      sponsorId: userData.sponsorId,
      referralsCount: userData.referrals?.length || 0
    });
    
    const node = {
      userId: userKey,
      id: userKey,
      name: userData.name || 'Unknown',
      position: depth === 0 ? 'head' : 'direct',
      status: isActive ? 'Active' : 'Inactive',
      package: getPackageName(userData),
      level: depth,
      firebaseKey: userKey,
      sponsorId: userData.sponsorId || 'Unknown',
      uplineSponsorId: userData.uplineSponsorId,
      referrals: userData.referrals || [],
      children: [],
      hasActivePackage: isActive,
      userData: userData,
      email: userData.email || 'N/A',
      phone: userData.mobile || 'N/A',
      location: userData.country || 'N/A',
      registrationDate: userData.registrationDate || 'N/A',
      showAllChildren: false
    };
    
    // Process referrals from the user's referrals array (same as MyDirects.js)
    const referrals = userData.referrals || [];
    
    if (depth < maxDepth - 1 && referrals.length > 0) {
      console.log(`Processing ${referrals.length} referrals for ${userData.name}`);
      
      for (const refItem of referrals) {
        let foundUser = null;
        let foundKey = null;
        
        // Try to find by dbKey first
        if (refItem.dbKey && allUsersData[refItem.dbKey]) {
          foundKey = refItem.dbKey;
          foundUser = allUsersData[refItem.dbKey];
          console.log(`Found referral by dbKey: ${foundKey} -> ${foundUser?.name}`);
        }
        // Then try by sponsorId
        else if (refItem.sponsorId) {
          for (const [key, user] of Object.entries(allUsersData)) {
            if (user.sponsorId === refItem.sponsorId) {
              foundKey = key;
              foundUser = user;
              console.log(`Found referral by sponsorId: ${foundKey} -> ${user?.name}`);
              break;
            }
          }
        }
        
        if (foundUser && foundKey && !visited.has(foundKey)) {
          const childNode = buildTreeFromReferrals(foundKey, foundUser, allUsersData, depth + 1, maxDepth, visited);
          if (childNode) {
            childNode.position = `member-${node.children.length + 1}`;
            node.children.push(childNode);
          }
        } else if (foundUser && foundKey && visited.has(foundKey)) {
          console.log(`Skipping ${foundUser.name} - already visited (circular reference detected)`);
        }
      }
      
      console.log(`User ${node.name} has ${node.children.length} direct referrals`);
    }
    
    return node;
  };

  // Calculate level statistics
  const calculateLevelStats = (treeNode) => {
    const levelStats = Array(fullDataDepth).fill().map((_, index) => ({
      level: index,
      total: 0,
      active: 0,
      inactive: 0,
      users: [],
      displayInTree: index < levelDisplayLimit
    }));
    
    const traverse = (node, currentDepth) => {
      if (!node || currentDepth >= fullDataDepth) return;
      
      const levelIndex = currentDepth;
      if (levelIndex >= 0 && levelIndex < fullDataDepth) {
        levelStats[levelIndex].total++;
        
        const userInfo = {
          userId: node.userId,
          name: node.name,
          active: node.status === 'Active',
          depth: currentDepth,
          package: node.package,
          position: node.position,
          email: node.email,
          phone: node.phone,
          dbStatus: node.userData?.status || 'unknown'
        };
        
        levelStats[levelIndex].users.push(userInfo);
        
        if (node.status === 'Active') {
          levelStats[levelIndex].active++;
        } else {
          levelStats[levelIndex].inactive++;
        }
      }
      
      // Continue traversing children
      for (const child of node.children) {
        traverse(child, currentDepth + 1);
      }
    };
    
    traverse(treeNode, 0);
    
    // Log statistics for debugging
    console.log('Level Statistics:');
    levelStats.forEach((level, idx) => {
      if (level.total > 0) {
        console.log(`Level ${idx}: Active=${level.active}, Inactive=${level.inactive}, Total=${level.total}`);
      }
    });
    
    return levelStats;
  };

  // Render tree function
  const renderTree = (node, depth = 0) => {
    if (!node) return null;
    
    // Check if we should show all levels or limited levels
    const shouldShow = showAllLevels ? true : depth < levelDisplayLimit;
    if (!shouldShow) return null;
    
    const isSelected = selectedMember?.userId === node.userId;
    const hasChildren = node.children && node.children.length > 0;
    const childrenCount = node.children?.length || 0;
    
    const getStatusBadgeClass = (status) => {
      switch (status.toLowerCase()) {
        case 'active': return 'bg-green-500/20 text-green-400 border-green-500/30';
        case 'inactive': return 'bg-red-500/20 text-red-400 border-red-500/30';
        default: return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
      }
    };
    
    const getLevelColor = (level) => {
      const colors = [
        'bg-yellow-500/20 border-yellow-500/50',
        'bg-blue-500/20 border-blue-500/50',
        'bg-green-500/20 border-green-500/50',
        'bg-purple-500/20 border-purple-500/50',
        'bg-pink-500/20 border-pink-500/50',
        'bg-indigo-500/20 border-indigo-500/50',
        'bg-teal-500/20 border-teal-500/50',
        'bg-orange-500/20 border-orange-500/50',
        'bg-cyan-500/20 border-cyan-500/50',
        'bg-violet-500/20 border-violet-500/50',
      ];
      return colors[level % colors.length] || 'bg-gray-500/20 border-gray-500/50';
    };
    
    return (
      <div key={node.userId} className="flex flex-col items-center">
        {/* Member Card */}
        <div
          className={`relative cursor-pointer transition-all duration-200 hover:scale-105 min-w-[140px] md:min-w-[160px] ${
            isSelected ? 'ring-2 ring-primary ring-offset-2 ring-offset-background' : ''
          }`}
          onClick={() => handleMemberClick(node)}
        >
          <div className={`card-glow p-3 md:p-4 rounded-lg md:rounded-xl ${getLevelColor(depth)}`}>
            <div className="flex items-center justify-between mb-1 md:mb-2">
              <div className="flex items-center">
                <div className={`icon-electric mr-2`}>
                  <User className={`h-3 w-3 md:h-4 md:w-4 ${
                    node.status === 'Active' ? 'text-green-400' : 'text-gray-400'
                  }`} />
                </div>
                <div className="max-w-[80px] md:max-w-[100px]">
                  <div className="font-bold text-white text-xs md:text-sm truncate">{node.name}</div>
                  <div className="text-[10px] md:text-xs text-muted-foreground font-mono truncate">{node.sponsorId || node.userId}</div>
                </div>
              </div>
              {node.position === 'head' && (
                <Crown className="h-3 w-3 md:h-4 md:w-4 text-yellow-400" />
              )}
            </div>
           
            <div className="flex items-center justify-between mb-1 md:mb-2">
              <span className={`inline-flex items-center px-1.5 py-0.5 md:px-2 md:py-1 rounded-full text-[10px] md:text-xs bg-secondary`}>
                {isMobile ? `L${node.level}` : `Level ${node.level}`}
              </span>
              <span className={`inline-flex items-center px-1.5 py-0.5 md:px-2 md:py-1 rounded-full text-[10px] md:text-xs font-medium ${getStatusBadgeClass(node.status)}`}>
                {node.status === 'Active' && <CheckCircle className="h-2.5 w-2.5 mr-0.5" />}
                {node.status === 'Inactive' && <XCircle className="h-2.5 w-2.5 mr-0.5" />}
                {isMobile ? node.status.charAt(0) : node.status}
              </span>
            </div>
           
            <div className="text-[10px] md:text-xs text-muted-foreground mb-0.5 md:mb-1">
              {isMobile ? `Ref: ${childrenCount}` : `Referrals: ${childrenCount}`}
            </div>
            <div className="text-[10px] md:text-xs font-semibold text-yellow-300 truncate">{isMobile ? node.package.split(' ')[0] : node.package}</div>
          </div>
          
          {/* Connection line to children */}
          {hasChildren && depth < (showAllLevels ? fullDataDepth - 1 : levelDisplayLimit - 1) && (
            <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-0.5 h-2 md:h-4 bg-primary/50"></div>
          )}
          
          {/* Level indicator */}
          <div className="absolute -top-2 -left-2 w-4 h-4 md:w-5 md:h-5 rounded-full bg-card border border-primary flex items-center justify-center">
            <span className="text-[8px] md:text-xs font-bold text-primary">{node.level}</span>
          </div>
        </div>
        
        {/* Children Container */}
        {hasChildren && depth < (showAllLevels ? fullDataDepth - 1 : levelDisplayLimit - 1) && (
          <>
            {/* Vertical connector line from parent to children row */}
            <div className="w-0.5 h-2 md:h-4 bg-primary/50"></div>
           
            {/* Children container with better layout */}
            <div className="mt-2 md:mt-4 relative">
              {/* Horizontal top connector line */}
              <div className="absolute top-0 left-0 right-0 h-0.5 bg-primary/50"></div>
             
              {/* Children grid - Responsive with better spacing */}
              <div className="flex flex-wrap justify-center gap-2 md:gap-4 relative">
                {node.children.map((child, index) => (
                  <div key={child.userId} className="relative">
                    {/* Individual vertical connector line for each child */}
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-0.5 h-2 md:h-4 bg-primary/50"></div>
                   
                    {/* Child tree */}
                    <div className="pt-2 md:pt-4">
                      {renderTree(child, depth + 1)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
        
        {/* Show "More Levels" indicator for nodes at level limit (if they have children beyond) */}
        {depth === levelDisplayLimit - 1 && !showAllLevels && node.children && node.children.length > 0 && (
          <div className="mt-2 md:mt-3 text-center">
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowAllLevels(true);
              }}
              className="inline-flex items-center px-3 py-1 bg-purple-500/20 border border-purple-500/50 rounded-lg hover:bg-purple-500/30 transition-colors"
            >
              <ChevronsRight className="h-3 w-3 mr-1 text-purple-300" />
              <span className="text-xs text-purple-300">
                Show {countChildrenBeyondLevel(node, depth)} more at deeper levels
              </span>
            </button>
          </div>
        )}
      </div>
    );
  };

  // Count children beyond current display level
  const countChildrenBeyondLevel = (node, currentDepth) => {
    let count = 0;
    
    const traverse = (n, depth) => {
      if (!n) return;
      
      if (depth > currentDepth) {
        count++;
      }
      
      for (const child of n.children) {
        traverse(child, depth + 1);
      }
    };
    
    // Start counting from children of current node
    for (const child of node.children) {
      traverse(child, currentDepth + 1);
    }
    
    return count;
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 2));
  };
  
  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
  };
  
  const handleResetZoom = () => {
    setZoomLevel(1);
  };
  
  const handleMemberClick = (member) => {
    setSelectedMember(member);
  };
  
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    fetchAllData();
  };

  const exportTreeImage = () => {
    alert('Export functionality coming soon!');
  };
  
  const refreshData = () => {
    setLoading(true);
    fetchAllData();
  };

  const toggleLevelInfo = () => {
    setShowLevelInfo(!showLevelInfo);
  };

  const toggleShowAllLevels = () => {
    setShowAllLevels(!showAllLevels);
  };

  // Render Level Statistics Panel
  const renderLevelStatsPanel = () => (
    <div className="card-glow p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-foreground flex items-center">
          <Layers className="h-4 w-4 mr-2" />
          Level Statistics
        </h3>
        <button
          onClick={toggleLevelInfo}
          className="text-xs text-primary hover:text-primary/80 flex items-center"
        >
          {showLevelInfo ? <EyeOff className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
          {showLevelInfo ? 'Hide' : 'Show'}
        </button>
      </div>
      
      {showLevelInfo && (
        <div className="space-y-2">
          {levels.slice(0, fullDataDepth).map((level, index) => (
            level.total > 0 && (
              <div key={index} className="flex items-center justify-between p-2 bg-secondary/50 rounded-lg">
                <div className="flex items-center">
                  <div className={`w-3 h-3 rounded-full mr-2 ${level.displayInTree ? 'bg-green-500' : 'bg-gray-500'}`}></div>
                  <span className="text-foreground font-medium">
                    Level {level.level}: 
                  </span>
                </div>
                <div className="text-sm text-muted-foreground">
                  <span className="text-green-400 font-bold">{level.active}</span> active / 
                  <span className="text-red-400 font-bold ml-1">{level.inactive}</span> inactive / 
                  <span className="text-primary font-bold ml-1">{level.total}</span> total
                </div>
              </div>
            )
          ))}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-full mx-auto pt-16 md:pt-20 px-3 sm:px-4">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-primary"></div>
          <span className="ml-4 text-muted-foreground">Loading enhanced network tree...</span>
        </div>
      </div>
    );
  }
  
  return (
    <div className="max-w-full mx-auto pt-16 md:pt-20 px-3 sm:px-4">
      {/* Header */}
      <div className="mb-6 md:mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between mb-4 md:mb-6">
          <div className="mb-3 md:mb-0">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-1 md:mb-2">Genealogy Tree</h1>
            <div className="flex items-center space-x-4">
              <button
                onClick={toggleShowAllLevels}
                className="px-3 py-1 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs flex items-center"
              >
                {showAllLevels ? (
                  <>
                    <ChevronUp className="h-3 w-3 mr-1" />
                    Show Less
                  </>
                ) : (
                  <>
                    <ChevronsRight className="h-3 w-3 mr-1" />
                    Show All Levels
                  </>
                )}
              </button>
            </div>
          </div>
          
          {/* Selected Member Info in Header */}
          {selectedMember && (
            <div className="card-glow p-3 md:p-4">
              <div className="flex flex-col md:flex-row md:items-center space-y-2 md:space-y-0 md:space-x-4">
                <div className="flex items-center">
                  <div className={`icon-electric mr-2 md:mr-3`}>
                    <User className={`h-4 w-4 md:h-5 md:w-5 ${
                      selectedMember.status === 'Active' ? 'text-green-400' : 'text-gray-400'
                    }`} />
                  </div>
                  <div>
                    <div className="font-bold text-foreground text-sm md:text-base truncate">{selectedMember.name}</div>
                    <div className="text-primary text-xs md:text-sm font-mono truncate">
                      {selectedMember.sponsorId || selectedMember.userId}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs md:text-sm">
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-2 py-0.5 md:px-3 md:py-1 rounded-full font-medium bg-secondary`}>
                      Level {selectedMember.level}
                    </span>
                  </div>
                  <div className="flex items-center">
                    <Mail className="h-3 w-3 md:h-4 md:w-4 mr-1 text-primary" />
                    <span className="text-muted-foreground truncate max-w-[100px] md:max-w-none">{selectedMember.email}</span>
                  </div>
                  <div className="flex items-center">
                    <Phone className="h-3 w-3 md:h-4 md:w-4 mr-1 text-primary" />
                    <span className="text-muted-foreground truncate">{selectedMember.phone}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      selectedMember.status === 'Active' ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                    }`}>
                      {selectedMember.status}
                    </span>
                    <span className="text-primary ml-2 font-semibold">
                      {selectedMember.package}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Level Info Panel */}
        {renderLevelStatsPanel()}
        
        {/* Controls */}
        <div className="card-glow p-3 md:p-4 mb-4 md:mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3 md:gap-4">
            <div className="flex items-center space-x-2 md:space-x-4 w-full md:w-auto">
              <div className="relative flex-1 md:flex-none">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search member..."
                  className="w-full pl-9 md:pl-10 pr-3 md:pr-4 py-2 bg-card border border-input rounded-lg md:rounded-xl text-foreground placeholder-muted-foreground focus:outline-none focus:border-primary text-sm"
                />
                <Search className="absolute left-2.5 md:left-3 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 md:h-4 md:w-4 text-primary" />
              </div>
             
              <select
                value={viewMode}
                onChange={(e) => handleViewModeChange(e.target.value)}
                className="px-3 md:px-4 py-2 bg-card border border-input rounded-lg md:rounded-xl text-foreground focus:outline-none focus:border-primary text-sm hidden md:block"
              >
                <option value="direct" className="bg-card">Direct View (All)</option>
                <option value="binary" className="bg-card">Binary View</option>
                <option value="level" className="bg-card">Level View</option>
              </select>
            </div>
            <div className="flex items-center space-x-1 md:space-x-2 w-full md:w-auto justify-between md:justify-normal">
              <div className="flex items-center space-x-1">
                <button
                  onClick={handleZoomOut}
                  className="p-1.5 md:p-2 hover:bg-secondary rounded-lg transition-colors"
                  title="Zoom Out"
                >
                  <ZoomOut className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                </button>
               
                <span className="text-foreground font-medium px-1 md:px-2 text-sm">{Math.round(zoomLevel * 100)}%</span>
               
                <button
                  onClick={handleZoomIn}
                  className="p-1.5 md:p-2 hover:bg-secondary rounded-lg transition-colors"
                  title="Zoom In"
                >
                  <ZoomIn className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                </button>
               
                <button
                  onClick={handleResetZoom}
                  className="p-1.5 md:p-2 hover:bg-secondary rounded-lg transition-colors hidden md:block"
                  title="Reset Zoom"
                >
                  <RefreshCw className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                </button>
                
                <button
                  onClick={refreshData}
                  className="p-1.5 md:p-2 hover:bg-secondary rounded-lg transition-colors"
                  title="Refresh Data"
                >
                  <RefreshCw className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                </button>
              </div>
             
              <button
                onClick={exportTreeImage}
                className="flex items-center px-3 md:px-4 py-2 btn-electric text-primary-foreground font-semibold rounded-lg md:rounded-xl transition-all text-sm"
              >
                <Download className="h-3.5 w-3.5 md:h-4 md:w-4 mr-1 md:mr-2" />
                {isMobile ? 'Export' : 'Export'}
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Error Message */}
      {errorMessage && (
        <div className="mb-4 md:mb-6 p-3 md:p-4 bg-red-500/20 border border-red-500/30 rounded-lg md:rounded-xl text-red-200 text-center text-sm md:text-base">
          {errorMessage}
          <button 
            onClick={refreshData}
            className="ml-2 px-3 py-1 bg-red-500 hover:bg-red-600 rounded text-sm"
          >
            Retry
          </button>
        </div>
      )}
      
      {/* Tree View - Full Width */}
      <div>
        <div className="card-glow p-4 md:p-6">
          <div className="flex items-center justify-between mb-4 md:mb-6">
            <h2 className="text-lg md:text-xl font-semibold text-foreground">
              {showAllLevels ? (
                <>Network Tree - Showing All {fullDataDepth} Levels</>
              ) : (
                <>Network Tree - Showing Levels 0-{levelDisplayLimit - 1}</>
              )}
            </h2>
            <div className="hidden md:flex items-center space-x-2">
              <button
                onClick={toggleShowAllLevels}
                className="px-3 py-1 bg-purple-500 hover:bg-purple-600 text-white rounded-lg text-xs flex items-center"
              >
                {showAllLevels ? (
                  <>
                    <ChevronUp className="h-3 w-3 mr-1" />
                    Show Less Levels
                  </>
                ) : (
                  <>
                    <ChevronsRight className="h-3 w-3 mr-1" />
                    Show All {fullDataDepth} Levels
                  </>
                )}
              </button>
            </div>
          </div>
          {treeData ? (
            <>
              {/* Tree Container with Horizontal Scroll */}
              <div
                ref={treeContainerRef}
                className="overflow-x-auto overflow-y-visible p-2 md:p-4 bg-card/50 rounded-lg md:rounded-xl min-h-[500px]"
              >
                {/* Inner Container with Zoom Applied */}
                <div 
                  className="min-w-max flex justify-center transition-transform duration-300 ease-in-out"
                  style={{
                    transform: `scale(${zoomLevel})`,
                    transformOrigin: 'top center'
                  }}
                >
                  <div className="w-full flex justify-center">
                    <div className="flex flex-col items-center">
                      {renderTree(treeData)}
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-4 md:mt-6">
                <div className="text-center text-xs text-muted-foreground">
                  Click on any member to view details | 
                  {showAllLevels ? (
                    <> Showing all {fullDataDepth} levels of data</>
                  ) : (
                    <> Tree shows up to Level {levelDisplayLimit - 1} | Click "Show All" to see remaining levels</>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-8 md:py-12">
              <Users className="h-8 w-8 md:h-12 md:w-12 text-primary/50 mx-auto mb-3 md:mb-4" />
              <p className="text-primary text-sm md:text-base">No network tree available. Start referring users to build your network.</p>
              <button 
                onClick={refreshData}
                className="mt-4 px-4 py-2 btn-electric text-primary-foreground font-semibold rounded-lg transition-all"
              >
                Refresh Data
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GenealogyTree;